/* Global collection order. Product cards remain rendered by the theme's Liquid snippet. */
(() => {
  'use strict';
  const stablePartition = (products) => products.filter((p) => p.available).concat(products.filter((p) => !p.available));
  const pageItems = (products, page, size) => products.slice((page - 1) * size, page * size);
  if (typeof module === 'object' && module.exports) {
    module.exports = { stablePartition, pageItems };
    return;
  }
  if (window.ifgAvailabilityOrderLoaded) return;
  window.ifgAvailabilityOrderLoaded = true;

  const VERSION = 'ifg-availability-v2:';
  const TTL = 60000;
  const indices = new Map();
  const cardPages = new Map();
  const runs = new WeakMap();
  const queue = [];
  let active = 0;
  function request(url) {
    return new Promise((resolve, reject) => {
      queue.push({ url, resolve, reject });
      drain();
    });
  }
  function drain() {
    while (active < 4 && queue.length) {
      const job = queue.shift();
      active++;
      fetch(job.url, { credentials: 'same-origin', signal: AbortSignal.timeout(20000) })
        .then((response) => {
          if (!response.ok) throw new Error('Collection request failed');
          return response.text();
        }).then(job.resolve, job.reject).finally(() => { active--; drain(); });
    }
  }
  function collectionURL(host) {
    const url = new URL(host.dataset.ifgKind === 'featured' ? host.dataset.ifgCollectionUrl : location.href, location.origin);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    url.searchParams.sort();
    return url;
  }
  function sectionURL(base, section, page) {
    const url = new URL(base);
    url.searchParams.set('section_id', section);
    url.searchParams.set('page', page);
    return url;
  }
  function readIndex(html) {
    const script = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ifg-availability-index]');
    if (!script) throw new Error('Missing collection index');
    return JSON.parse(script.textContent);
  }
  function invalidate(base) {
    indices.delete(base.href);
    for (const key of cardPages.keys()) if (key.startsWith(base.href + '|')) cardPages.delete(key);
    try { sessionStorage.removeItem(VERSION + base.href); } catch (_) { /* Storage can be unavailable. */ }
  }
  async function loadIndex(base) {
    if (indices.has(base.href)) return indices.get(base.href);
    const pending = (async () => {
      try {
        const cached = JSON.parse(sessionStorage.getItem(VERSION + base.href));
        if (cached && Date.now() - cached.created < TTL && Array.isArray(cached.products)) return cached.products;
      } catch (_) { /* A fresh index is the fallback. */ }
      const first = readIndex(await request(sectionURL(base, 'ifg-availability-index', 1)));
      if (!Number.isInteger(first.total) || first.total > 25000 || first.page !== 1) throw new Error('Invalid collection count');
      const rest = await Promise.all(Array.from({ length: Math.max(0, first.pages - 1) }, async (_, i) => {
        const data = readIndex(await request(sectionURL(base, 'ifg-availability-index', i + 2)));
        if (data.total !== first.total || data.page !== i + 2) throw new Error('Collection changed during loading');
        return data.products;
      }));
      const products = [first.products, ...rest].flat().map((p, i) => ({ ...p, sourcePage: Math.floor(i / 50) + 1 }));
      if (products.length !== first.total || new Set(products.map((p) => p.id)).size !== first.total || products.some((p) => typeof p.available !== 'boolean')) {
        throw new Error('Incomplete collection index');
      }
      try {
        for (const key of Object.keys(sessionStorage)) {
          if (key.startsWith(VERSION) && Date.now() - JSON.parse(sessionStorage.getItem(key)).created >= TTL) sessionStorage.removeItem(key);
        }
        sessionStorage.setItem(VERSION + base.href, JSON.stringify({ created: Date.now(), products }));
      } catch (_) { /* The ordering also works without persistent cache. */ }
      return products;
    })();
    indices.set(base.href, pending);
    try { return await pending; } catch (error) { indices.delete(base.href); throw error; }
  }
  async function loadCards(base, page) {
    const key = base.href + '|' + page;
    if (!cardPages.has(key)) cardPages.set(key, request(sectionURL(base, 'ifg-availability-cards', page)).then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const result = new Map();
      doc.querySelectorAll('template[data-ifg-product-id]').forEach((template) => result.set(template.dataset.ifgProductId, template));
      if (!result.size) throw new Error('Missing product cards');
      return result;
    }));
    return cardPages.get(key);
  }
  function replaceCards(host, grid, products, templates, offset) {
    const featured = host.dataset.ifgKind === 'featured';
    const nodes = products.map((product, i) => {
      const node = document.createElement(featured ? 'div' : 'li');
      node.className = featured ? host.dataset.ifgColumns : 'column';
      node.dataset.ifgProductId = product.id;
      node.dataset.ifgAvailable = String(product.available);
      node.dataset.ifgOrdinal = String(offset + i + 1);
      node.append(templates.get(product.id).content.cloneNode(true));
      return node;
    });
    const carousel = featured && grid.tagName === 'SLIDE-SHOW';
    const flickity = carousel && window.Flickity && window.Flickity.data(grid);
    if (flickity) {
      flickity.remove(flickity.getCellElements());
      flickity.append(nodes);
      flickity.select(0, false, true);
      flickity.resize();
    } else if (carousel) {
      grid.querySelectorAll('.carousel__slide').forEach((node) => node.remove());
      grid.prepend(...nodes);
    } else {
      const fragment = document.createDocumentFragment();
      const promotions = new Map();
      let position = 0;
      Array.from(grid.children).forEach((node) => {
        if (node.classList.contains('promotion-column') || node.tagName === 'LINK') {
          if (!promotions.has(position)) promotions.set(position, []);
          promotions.get(position).push(node);
        } else if (node.tagName === 'LI') position++;
      });
      nodes.forEach((node, i) => { (promotions.get(i) || []).forEach((promo) => fragment.append(promo)); fragment.append(node); });
      (promotions.get(nodes.length) || []).forEach((promo) => fragment.append(promo));
      grid.replaceChildren(fragment);
    }
    window.dispatchEvent(new Event('resize'));
    if (window.lazySizes) window.lazySizes.loader.checkElems();
  }
  async function render(host, grid, base, run) {
    const status = host.querySelector('[data-ifg-order-status]');
    host.dataset.ifgOrderState = 'loading';
    grid.setAttribute('aria-busy', 'true');
    status.textContent = 'Caricamento prodotti…';
    status.hidden = false;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const products = await loadIndex(base);
        const ordered = stablePartition(products);
        const size = Number(host.dataset.ifgPageSize);
        const pages = Math.max(1, Math.ceil(products.length / size));
        const page = host.dataset.ifgKind === 'featured' ? 1 : Math.min(pages, Math.max(1, Number(new URL(location.href).searchParams.get('page')) || 1));
        const wanted = pageItems(ordered, page, size);
        const sourcePages = [...new Set(wanted.map((p) => p.sourcePage))];
        const batches = await Promise.all(sourcePages.map((p) => loadCards(base, p)));
        const templates = new Map(batches.flatMap((batch) => [...batch]));
        if (wanted.some((p) => !templates.has(p.id) || templates.get(p.id).dataset.ifgAvailable !== String(p.available))) throw new Error('Availability changed');
        if (runs.get(host) !== run || !grid.isConnected) return;
        replaceCards(host, grid, wanted, templates, (page - 1) * size);
        host.dataset.ifgTotal = String(products.length);
        host.dataset.ifgAvailableCount = String(products.filter((p) => p.available).length);
        host.dataset.ifgCurrentPage = String(page);
        host.dataset.ifgOrderState = 'ready';
        grid.removeAttribute('aria-busy');
        status.textContent = '';
        status.hidden = true;
        return;
      } catch (error) {
        invalidate(base);
        if (runs.get(host) !== run || !grid.isConnected) return;
        if (attempt === 1) {
          host.dataset.ifgOrderState = 'error';
          grid.removeAttribute('aria-busy');
          status.textContent = 'Non è stato possibile ordinare per disponibilità. Ricarica la pagina per riprovare.';
          console.warn('IFG availability order:', error.message);
        }
      }
    }
  }
  function scan() {
    document.querySelectorAll('[data-ifg-order]').forEach((host) => {
      const grid = host.querySelector('#' + CSS.escape(host.dataset.ifgGridId));
      if (!grid || !host.dataset.ifgCollectionUrl) return;
      const base = collectionURL(host);
      const signature = base.href + '|' + (host.dataset.ifgKind === 'featured' ? '1' : location.search);
      const previous = runs.get(host);
      if (previous && previous.grid === grid && previous.signature === signature) return;
      const run = { grid, signature };
      runs.set(host, run);
      render(host, grid, base, run);
    });
  }
  let scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => { scheduled = false; scan(); });
  }
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', schedule);
  window.addEventListener('popstate', schedule);
  scan();
})();